import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Storage URL for images/files
// Use REACT_APP_STORAGE_URL if set, otherwise derive from API_URL
// - Live server uses /storage/app/public/ (direct file access)
// - When /storage/ route works, change to: API_URL.replace(/\/api$/, '') + '/storage'
export const STORAGE_URL = process.env.REACT_APP_STORAGE_URL
    || API_URL.replace(/\/api$/, '') + '/storage/app/public';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Accept': 'application/json',
    },
    withCredentials: false,
});

// Request interceptor - add auth token and handle FormData
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Let axios set Content-Type automatically for FormData
        if (!(config.data instanceof FormData)) {
            config.headers['Content-Type'] = 'application/json';
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor - handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            // Don't redirect on public routes (verify page)
            const isPublicRoute = error.config?.url?.includes('/verify/');
            if (!isPublicRoute) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (credentials) => api.post('/login', credentials),
    logout: () => api.post('/logout'),
    user: () => api.get('/user'),
};

// Dashboard API
export const dashboardAPI = {
    stats: () => api.get('/dashboard/stats'),
};

// Staff API
export const staffAPI = {
    list: (params) => api.get('/staff', { params }),
    create: (data) => api.post('/staff', data),
    update: (id, data) => api.put(`/staff/${id}`, data),
    delete: (id) => api.delete(`/staff/${id}`),
    toggleStatus: (id) => api.put(`/staff/${id}/status`),
};

// Certificate API
export const certificateAPI = {
    list: (params) => api.get('/certificates', { params }),
    get: (id) => api.get(`/certificates/${id}`),
    create: (data) => {
        // Check if data is already FormData
        const formData = data instanceof FormData ? data : (() => {
            const fd = new FormData();
            Object.keys(data).forEach((key) => {
                if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
                    fd.append(key, data[key]);
                }
            });
            return fd;
        })();
        // Don't set Content-Type - let axios handle it with correct boundary
        return api.post('/certificates', formData);
    },
    update: (id, data) => {
        const formData = data instanceof FormData ? data : (() => {
            const fd = new FormData();
            Object.keys(data).forEach((key) => {
                if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
                    fd.append(key, data[key]);
                }
            });
            return fd;
        })();
        formData.append('_method', 'PUT');
        return api.post(`/certificates/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    search: (params) => api.get('/certificates/search', { params }),
    downloadPdf: (id) => api.get(`/certificates/${id}/pdf`, { responseType: 'blob' }),
    whatsappLink: (id) => api.post(`/certificates/${id}/whatsapp`),
};

// Template API
export const templateAPI = {
    list: () => api.get('/templates'),
    create: (data) => api.post('/templates', data),
    update: (id, data) => api.put(`/templates/${id}`, data),
    delete: (id) => api.delete(`/templates/${id}`),
};

// Settings API
export const settingsAPI = {
    get: () => api.get('/settings'),
    uploadLogo: (formData) => api.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    uploadFavicon: (formData) => api.post('/settings/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    uploadStamp: (formData) => api.post('/settings/stamp', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    uploadAuthoritySignature: (formData) => api.post('/settings/authority-signature', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    updateOffice: (data) => api.put('/settings/office', data),
    deleteLogo: () => api.delete('/settings/logo'),
    deleteFavicon: () => api.delete('/settings/favicon'),
    deleteStamp: () => api.delete('/settings/stamp'),
    deleteAuthoritySignature: () => api.delete('/settings/authority-signature'),
};

// Verification API
export const verificationAPI = {
    verify: (certificateNo) => api.get(`/verify/${certificateNo}`),
};

// Transliteration API
export const transliterationAPI = {
    toMarathi: (text) => api.post('/transliterate/to-marathi', { text }),
    batchToMarathi: (texts) => api.post('/transliterate/batch-to-marathi', { texts }),
};

// Report API
export const reportAPI = {
    daily: (params) => api.get('/reports/daily', { params }),
    weekly: (params) => api.get('/reports/weekly', { params }),
    monthly: (params) => api.get('/reports/monthly', { params }),
    yearly: (params) => api.get('/reports/yearly', { params }),
    myStats: () => api.get('/reports/my-stats'),
    staffReport: (id) => api.get(`/reports/staff/${id}`),
    exportPdf: (params) => api.get('/reports/export/pdf', { params, responseType: 'blob' }),
    exportExcel: (params) => api.get('/reports/export/excel', { params, responseType: 'blob' }),
};

export default api;